import { Button, CheckIcon, CompareIcon, Typography } from '@iheartjane/reefer';
import type { ProductCardSize } from '@iheartjane/types';
import { clsx } from 'clsx';
import type { MouseEvent } from 'react';

import styles from './compareToggle.module.css';

interface CompareToggleProps {
  cardSize?: ProductCardSize;
  disabled?: boolean;
  isSelected: boolean;
  onToggle: () => void;
  /**
   * Position of the `labeled` pill:
   * `overlay` = bottom-left of the image
   * `inline` = bottom of the card
   */
  placement?: 'overlay' | 'inline';
  productName: string;
  /**
   * `icon` (default) = original icon button
   * `labeled` = pill used by Premium
   */
  variant?: 'icon' | 'labeled';
}

export const CompareToggle = ({
  cardSize = 'medium',
  disabled = false,
  isSelected,
  onToggle,
  placement = 'overlay',
  productName,
  variant = 'icon',
}: CompareToggleProps) => {
  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle();
  };

  // TODO: replace this with a shared Reefer
  // Pill/Chip (selectable variant) once it exists
  // https://www.figma.com/design/acoTASmDAGFqgCe4buL7CE/Reefer?node-id=26418-7173
  if (variant === 'labeled') {
    return (
      <button
        type="button"
        className={clsx(styles['pill'], styles[`pill_${placement}`], {
          [styles['pill__active']]: isSelected,
          [styles['pill__disabled']]: disabled,
          [styles['pill__large']]: cardSize === 'large',
        })}
        disabled={disabled}
        onClick={handleClick}
        aria-label={`Compare ${productName}`}
        aria-pressed={isSelected}
      >
        <span className={styles['indicator']} aria-hidden="true" />
        <Typography
          as="span"
          variant="body"
          color="grays-black"
          className={styles['label']}
        >
          Compare
        </Typography>
      </button>
    );
  }

  return (
    <Button.Icon
      className={clsx(styles['toggle'], {
        [styles['toggle__active']]: isSelected,
      })}
      disabled={disabled}
      icon={isSelected ? <CheckIcon size="sm" /> : <CompareIcon size="sm" />}
      onClick={handleClick}
      aria-label={`Compare ${productName}`}
      aria-pressed={isSelected}
    />
  );
};
